import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

export interface FetchState<T> {
  data: T | null;
  loading: boolean;
  error: boolean;
}

/**
 * Runs `fetcher` whenever `deps` change, ignores responses that arrive after a
 * newer request started, and toasts once per failure.
 */
export function useAnalyticsFetch<T>(
  fetcher: () => Promise<T>,
  deps: ReadonlyArray<unknown>,
  errorMessage = "Could not load analytics"
): FetchState<T> {
  const [state, setState] = useState<FetchState<T>>({ data: null, loading: true, error: false });
  const requestId = useRef(0);

  useEffect(() => {
    const id = ++requestId.current;
    setState((prev) => ({ ...prev, loading: true, error: false }));
    fetcher()
      .then((data) => {
        if (id === requestId.current) setState({ data, loading: false, error: false });
      })
      .catch((err) => {
        if (id !== requestId.current) return;
        console.error(err?.response || err);
        toast.error(err?.response?.data?.message || errorMessage);
        setState({ data: null, loading: false, error: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}
